import clsx from 'clsx';

export interface ActionButtonClusterProps {
  /**
   * Submit a synthetic prompt that asks `compare_scenarios` for the
   * compact_3axis variant (Downside / Base / Upside summary). Caller
   * routes through the existing `handleSend` / streaming path.
   */
  onRunScenario: () => void;
  /**
   * Download the provenance audit-trail JSON for the active conversation
   * (`GET /api/conversations/:id/provenance`). Caller wires the actual
   * fetch + blob download.
   */
  onDownloadProvenance: () => void;
  /** Disabled while a stream is in-flight or no inputs are set. */
  runScenarioDisabled?: boolean;
  /** Disabled when no underwriting events / not yet acceptable. */
  provenanceDisabled?: boolean;
  className?: string;
}

interface ActionButton {
  testId: string;
  label: string;
  onClick: () => void;
  disabled: boolean;
  variant: 'primary' | 'secondary';
}

/**
 * Right-panel action cluster (Phase 3 Session 4, restructured 2026-05-07).
 *
 * Two buttons, both always visible:
 * - **Run Scenario Analysis** — fires `compare_scenarios` with
 *   `kind: 'compact_3axis'` for the Downside/Base/Upside summary card.
 * - **View Provenance Report** — downloads the provenance JSON audit
 *   trail for the active conversation.
 *
 * The earlier Generate Excel Model button was removed when the
 * Excel-generation user action was retired (2026-05-07): the model is no
 * longer downloadable from the Builder. The agent tool `generate_excel`
 * remains internally available for autonomous use but is no longer
 * user-triggerable from the UI.
 */
export function ActionButtonCluster({
  onRunScenario,
  onDownloadProvenance,
  runScenarioDisabled = false,
  provenanceDisabled = false,
  className,
}: ActionButtonClusterProps) {
  const buttons: ActionButton[] = [
    {
      testId: 'action-run-scenario',
      label: 'Run Scenario Analysis',
      onClick: onRunScenario,
      disabled: runScenarioDisabled,
      variant: 'primary',
    },
    {
      testId: 'action-provenance',
      label: 'View Provenance Report',
      onClick: onDownloadProvenance,
      disabled: provenanceDisabled,
      variant: 'secondary',
    },
  ];

  return (
    <div
      data-testid="action-button-cluster"
      className={clsx('flex flex-col gap-2', className)}
    >
      {buttons.map((b) => (
        <button
          key={b.testId}
          type="button"
          data-testid={b.testId}
          onClick={b.onClick}
          disabled={b.disabled}
          className={clsx(
            'w-full py-2.5 rounded-[3px] font-mono text-sm font-semibold transition-all duration-200',
            b.variant === 'primary'
              ? 'bg-accent-dim border border-accent/20 text-accent hover:bg-accent/13'
              : 'bg-input-bg border border-panel-border text-text hover:border-accent/30',
            'disabled:opacity-50 disabled:cursor-not-allowed',
          )}
        >
          {b.label}
        </button>
      ))}
    </div>
  );
}
